import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, PawPrint, ChevronDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const emptyClient = { name: "", email: "", phone: "", notes: "" };
const emptyPet = { name: "", species: "Dog", breed: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyClient);
  const [expanded, setExpanded] = useState(null);
  const [petForm, setPetForm] = useState(emptyPet);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPet = (k) => (e) => setPetForm({ ...petForm, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/clients", form); toast.success("Client added"); setOpen(false); setForm(emptyClient); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/clients/${id}`); toast.success("Client removed"); load(); };
  const addPet = async (clientId) => {
    if (!petForm.name) return toast.error("Pet name required");
    await api.post(`/clients/${clientId}/pets`, petForm); toast.success("Pet added"); setPetForm(emptyPet); load();
  };
  const delPet = async (clientId, petId) => { await api.delete(`/clients/${clientId}/pets/${petId}`); load(); };

  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-4xl">Clients & Pets</h1><p className="text-brand-ink/60 mt-2">Your community and their companions.</p></div>
        <button onClick={() => { setForm(emptyClient); setOpen(true); }} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="add-client-btn"><Plus className="w-4 h-4" /> Add client</button>
      </div>

      <div className="mt-8 space-y-3">
        {clients.length === 0 && <p className="text-brand-ink/50" data-testid="clients-empty">No clients yet.</p>}
        {clients.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-brand-line overflow-hidden" data-testid={`client-${c.id}`}>
            <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
              <div>
                <div className="font-medium">{c.name}{c.suspended && <span className="text-xs text-red-600 ml-2">Suspended</span>}</div>
                <div className="text-sm text-brand-ink/50">{c.email} · {c.phone || "no phone"} · {(c.pets || []).length} pet(s)</div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={(e) => { e.stopPropagation(); api.post(`/admin/customers/${c.id}/suspend`).then(() => { toast.success(c.suspended ? "Reactivated" : "Suspended"); load(); }); }} className="text-xs px-2 py-1 rounded border border-brand-line hover:border-brand-red transition-colors" data-testid={`suspend-client-${c.id}`}>{c.suspended ? "Reactivate" : "Suspend"}</button>
                <button onClick={(e) => { e.stopPropagation(); del(c.id); }} className="text-brand-ink/30 hover:text-brand-red transition-colors" data-testid={`delete-client-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                <ChevronDown className={`w-5 h-5 text-brand-ink/40 transition-transform ${expanded === c.id ? "rotate-180" : ""}`} />
              </div>
            </div>
            {expanded === c.id && (
              <div className="px-5 pb-5 border-t border-brand-line pt-4" data-testid={`client-pets-${c.id}`}>
                <div className="space-y-2 mb-4">
                  {(c.pets || []).map((p) => (
                    <div key={p.id} className="flex items-center justify-between bg-brand-mist rounded-lg px-4 py-2.5" data-testid={`pet-${p.id}`}>
                      <span className="flex items-center gap-2 text-sm"><PawPrint className="w-4 h-4 text-brand-red" /> {p.name} <span className="text-brand-ink/40">· {p.species}{p.breed ? ` · ${p.breed}` : ""}</span></span>
                      <button onClick={() => delPet(c.id, p.id)} className="text-brand-ink/30 hover:text-brand-red"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  {(c.pets || []).length === 0 && <p className="text-sm text-brand-ink/40">No pets on file.</p>}
                </div>
                <div className="flex gap-2 flex-wrap items-end">
                  <input placeholder="Pet name" value={petForm.name} onChange={setPet("name")} className="rounded-lg border border-brand-line px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={`pet-name-input-${c.id}`} />
                  <input placeholder="Species" value={petForm.species} onChange={setPet("species")} className="rounded-lg border border-brand-line px-3 py-2 text-sm w-28 focus:ring-2 focus:ring-brand-red focus:outline-none" />
                  <input placeholder="Breed" value={petForm.breed} onChange={setPet("breed")} className="rounded-lg border border-brand-line px-3 py-2 text-sm w-32 focus:ring-2 focus:ring-brand-red focus:outline-none" />
                  <button onClick={() => addPet(c.id)} className="px-4 py-2 rounded-lg bg-brand-ink text-white text-sm hover:bg-black transition-colors" data-testid={`add-pet-btn-${c.id}`}>Add pet</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="client-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-8">
            <div className="flex items-center justify-between mb-6"><h2 className="font-display text-2xl">Add client</h2><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={save} className="space-y-4">
              <Fld label="Name" v={form.name} on={set("name")} req testid="client-name" />
              <Fld label="Email" type="email" v={form.email} on={set("email")} req testid="client-email" />
              <Fld label="Phone" v={form.phone} on={set("phone")} testid="client-phone" />
              <div><label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">Notes</label><textarea value={form.notes} onChange={set("notes")} rows={2} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid="client-notes" /></div>
              <button type="submit" className="w-full px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="client-save-btn">Add client</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Fld({ label, type = "text", v, on, req, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">{label}</label>
      <input type={type} value={v} onChange={on} required={req} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={testid} />
    </div>
  );
}
